import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAccounts } from '../lib/useAccounts';
import { useWorkflow } from '../WorkflowContext';
import MonthCalendar from '../components/MonthCalendar';
import Tag from '../components/Tag';
import ClientEditor from '../components/ClientEditor';
import { shortLocation, type Account } from '../lib/accounts';
import { ordersFor, quotesFor, isDemoQuery, usd } from '../lib/clientDeepData';
import { followUps, reorderCandidates } from '../lib/salesData';
import { tasksToEvents, followUpsToEvents, reordersToEvents, meetingsToEvents } from '../lib/calendarData';

function MiniRing({ value }: { value: number }) {
  const r = 15, c = 2 * Math.PI * r, off = c * (1 - value / 100);
  const color = value >= 80 ? 'var(--gn)' : value >= 60 ? 'var(--or)' : 'var(--rd)';
  return (
    <svg viewBox="0 0 36 36" className="mini-ring">
      <circle cx="18" cy="18" r={r} fill="none" stroke="var(--bg4)" strokeWidth="3.5" />
      <circle cx="18" cy="18" r={r} fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 18 18)" />
      <text x="18" y="21.5" textAnchor="middle" fontSize="10" fontWeight="800" fill="var(--tx)">{value}</text>
    </svg>
  );
}

// Deterministic 0..1 hash so derived figures stay stable per client.
function hashNum(s: string): number {
  let h = 2166136261;
  for (const ch of s) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619); }
  return (h >>> 0) / 4294967296;
}

interface TileStat { activeOrders: number; openQuotes: number; quoteValue: number; health: number; tier: 'high' | 'mid' | 'low'; lastActive: string; }
function tileStats(c: Account, demo: boolean): TileStat {
  const orders = demo ? ordersFor(c.id) : [];
  const quotes = demo ? quotesFor(c.id) : [];
  const quoteValue = quotes.reduce((s, q) => s + q.value, 0);
  const jitter = Math.round(hashNum(c.id + 'h') * 14) - 7;
  const health = c.healthScore ?? Math.max(48, Math.min(98, 66 + orders.length * 6 + (quotes.length ? 5 : 0) + jitter));
  const tier: TileStat['tier'] = health >= 80 ? 'high' : health >= 62 ? 'mid' : 'low';
  const lastActive = ['Today', 'Yesterday', '2 days ago', '3 days ago', 'Last week'][Math.floor(hashNum(c.id + 'a') * 5)];
  return { activeOrders: orders.length, openQuotes: quotes.length, quoteValue, health, tier, lastActive };
}

export default function Clients() {
  const { demo } = useAuth();
  const nav = useNavigate();
  const { data, loading } = useAccounts(demo);
  const wf = useWorkflow();
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<'name' | 'health' | 'tasks'>('name');
  const [editor, setEditor] = useState(false);

  const demoData = isDemoQuery();
  const clients = useMemo(() => (data?.accounts ?? []).filter((a) => a.type !== 'vendor'), [data]);
  const openFor = (name: string) => wf.tasks.filter((t) => t.client === name && t.status !== 'completed').length;
  const statById = useMemo(() => {
    const m = new Map<string, TileStat>();
    clients.forEach((c) => m.set(c.id, tileStats(c, demoData)));
    return m;
  }, [clients, demoData]);

  const calEvents = useMemo(() => {
    const accounts = data?.accounts ?? [];
    return [
      ...tasksToEvents(wf.tasks),
      ...followUpsToEvents(followUps(accounts)),
      ...reordersToEvents(reorderCandidates(accounts)),
      ...meetingsToEvents(wf.events),
    ];
  }, [wf.tasks, wf.events, data]);

  const open = wf.tasks.filter((t) => t.status === 'acknowledged' || t.status === 'in_progress');
  const pending = wf.tasks.filter((t) => t.status === 'assigned');
  const blocked = wf.tasks.filter((t) => t.status === 'blocked');
  const upcoming = wf.tasks.filter((t) => t.status !== 'completed');
  const states = new Set(clients.map((c) => c.billing?.state || c.shipping?.state).filter(Boolean)).size;

  const tiles = useMemo(() => {
    let list = clients;
    if (q.trim()) {
      const t = q.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(t) || shortLocation(c).toLowerCase().includes(t) || (c.industry || '').toLowerCase().includes(t) || (c.mfxId || '').toLowerCase().includes(t));
    }
    const arr = [...list];
    if (sort === 'health') arr.sort((a, b) => (statById.get(b.id)?.health ?? 0) - (statById.get(a.id)?.health ?? 0));
    else if (sort === 'tasks') arr.sort((a, b) => openFor(b.name) - openFor(a.name));
    return arr;
  }, [clients, q, sort, wf.tasks, statById]);

  if (loading) return <div className="loading"><div className="spinner" /> Loading clients…</div>;
  const shown = tiles.slice(0, 60);

  return (
    <>
      {/* Hero */}
      <div className="hero">
        <div className="hero-text">
          <div className="hero-eyebrow">Client Services · Command Center</div>
          <h1 className="hero-title">Clients</h1>
          <p className="hero-sub">Upcoming work, open &amp; pending tasks, the shared calendar, and every client profile — in one place.</p>
        </div>
        <div className="hero-actions">
          {data && (data.live ? <span className="live-flag">Live · {data.sources.join(' + ')}</span> : <span className="demo-flag">Demo data</span>)}
          <button className="btn" onClick={() => nav('/intake')}>＋ New request</button>
          <button className="btn primary" onClick={() => setEditor(true)}>＋ New client</button>
        </div>
      </div>

      {/* KPI band */}
      <div className="stat-band">
        <div className="stat hero-stat"><div className="stat-v">{clients.length}</div><div className="stat-l">Total Clients</div><div className="stat-spark" /></div>
        <div className="stat"><div className="stat-v">{open.length}</div><div className="stat-l">Open Tasks</div><div className="stat-note">Acknowledged &amp; in progress</div></div>
        <div className="stat"><div className="stat-v" style={{ color: 'var(--yl)' }}>{pending.length}</div><div className="stat-l">Pending</div><div className="stat-note">Awaiting acknowledgment</div></div>
        <div className="stat"><div className="stat-v" style={{ color: 'var(--rd)' }}>{blocked.length}</div><div className="stat-l">Blocked</div><div className="stat-note">Needs info / material</div></div>
        <div className="stat"><div className="stat-v" style={{ color: 'var(--ac)' }}>{states}</div><div className="stat-l">States</div><div className="stat-note">Geographic reach</div></div>
      </div>

      {/* Upcoming + Recent */}
      <div className="grid cols-2" style={{ marginTop: 6 }}>
        <div className="card">
          <div className="card-header"><div><h3>Upcoming</h3><p>Open work ordered by commitment.</p></div></div>
          <div className="card-body"><div className="mini-list">
            {upcoming.length === 0 && <div className="sub">Nothing upcoming.</div>}
            {upcoming.map((t) => (
              <div className="mini-row" key={t.id} onClick={() => { const c = clients.find((x) => x.name === t.client); if (c) nav(`/client?id=${encodeURIComponent(c.id)}`); }} style={{ cursor: 'pointer' }}>
                <div className="mr-main"><strong>{t.title}</strong><div className="mr-sub">{t.client} · {t.owner}</div></div>
                <div style={{ textAlign: 'right' }}><div style={{ fontSize: 12, color: 'var(--ac)' }}>{t.due}</div><Tag kind="dp" value={t.dept} /></div>
              </div>
            ))}
          </div></div>
        </div>
        <div className="card">
          <div className="card-header"><div><h3>Recent Activity</h3><p>Latest synchronized client events.</p></div></div>
          <div className="card-body"><div className="timeline">
            {wf.timeline.slice(0, 6).map((i, idx) => (
              <div className="timeline-item" key={idx}><div className="time">{i.time}</div><div><strong>{i.title}</strong><p>{i.text}</p></div></div>
            ))}
          </div></div>
        </div>
      </div>

      {/* Open + Pending */}
      <div className="grid cols-2" style={{ marginTop: 16 }}>
        <div className="card">
          <div className="card-header"><div><h3>Open Tasks</h3><p>Actively being worked.</p></div><button className="btn small" onClick={() => nav('/tasks')}>Board →</button></div>
          <div className="card-body"><div className="mini-list">
            {open.length === 0 && <div className="sub">No open tasks.</div>}
            {open.map((t) => (<div className="mini-row" key={t.id}><div className="mr-main"><strong>{t.title}</strong><div className="mr-sub">{t.client} · due {t.due}</div></div><Tag kind="st" value={t.status} /></div>))}
          </div></div>
        </div>
        <div className="card">
          <div className="card-header"><div><h3>Pending &amp; Blocked</h3><p>Awaiting acknowledgment or resolution.</p></div></div>
          <div className="card-body"><div className="mini-list">
            {[...pending, ...blocked].length === 0 && <div className="sub">Nothing pending.</div>}
            {[...pending, ...blocked].map((t) => (<div className="mini-row" key={t.id}><div className="mr-main"><strong>{t.title}</strong><div className="mr-sub">{t.client} · {t.owner}</div></div><Tag kind="st" value={t.status} /></div>))}
          </div></div>
        </div>
      </div>

      {/* Calendar */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><div><h3>Client Calendar</h3><p>Tasks, follow-ups, meetings &amp; reorder windows — synced with Google Calendar.</p></div><button className="btn small" onClick={() => nav('/calendar')}>Full calendar →</button></div>
        <div className="card-body"><MonthCalendar crmEvents={calEvents} /></div>
      </div>

      {/* All Clients */}
      <div className="section-title-row" style={{ marginTop: 26 }}>
        <div><h2 className="section-title">All Clients</h2><p className="page-sub">{tiles.length} of {clients.length} · click any client to open the full profile</p></div>
        <div className="toolbar" style={{ margin: 0 }}>
          <input className="search" style={{ minWidth: 240 }} placeholder="Search name, MFX id, city, industry…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="seg">
            {(['name', 'health', 'tasks'] as const).map((s) => <button key={s} className={sort === s ? 'on' : ''} onClick={() => setSort(s)}>{s === 'name' ? 'A–Z' : s === 'health' ? 'Health' : 'Tasks'}</button>)}
          </div>
          <button className="btn primary" onClick={() => setEditor(true)}>＋ New client</button>
        </div>
      </div>

      <div className="client-grid2">
        {shown.map((c: Account) => {
          const ot = openFor(c.name);
          const s = statById.get(c.id) ?? tileStats(c, demoData);
          const loc = shortLocation(c);
          const meta = [c.mfxId, loc !== '—' ? loc : null, c.industry].filter(Boolean).join(' · ') || 'Client account';
          return (
            <button className={`ctile tier-${s.tier}`} key={c.id} onClick={() => nav(`/client?id=${encodeURIComponent(c.id)}`)}>
              <span className="ctile-bar" aria-hidden />
              <div className="ctile-top">
                <div className="ctile-av">{c.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}</div>
                <div className="ctile-id">
                  <div className="ctile-name">{c.name}</div>
                  <div className="ctile-loc">{meta}</div>
                </div>
                <MiniRing value={s.health} />
              </div>
              <div className="ctile-stats">
                <div className="ctile-stat"><b>{s.activeOrders}</b><span>Active orders</span></div>
                <div className="ctile-stat"><b>{s.quoteValue ? usd(s.quoteValue) : '—'}</b><span>Open quotes</span></div>
                <div className="ctile-stat"><b className={ot ? 'hot' : ''}>{ot}</b><span>Open tasks</span></div>
              </div>
              <div className="ctile-foot">
                <span className="tag dp-csr">CSR</span>
                <span className="ctile-active"><i className="dot" /> {s.lastActive}</span>
                <span className="ctile-go">Open →</span>
              </div>
            </button>
          );
        })}
      </div>
      {tiles.length > 60 && <div className="page-sub" style={{ textAlign: 'center', marginTop: 14 }}>Showing first 60 — refine your search to see more.</div>}

      {editor && (
        <ClientEditor
          mode="create"
          onClose={() => setEditor(false)}
          onSaved={(a) => { setEditor(false); nav(`/client?id=${encodeURIComponent(a.id)}`); }}
        />
      )}
    </>
  );
}
